import { useState } from "react";

import { Button, MessageModal, ModalWrapper } from "../ui";
import { ANNOUNCEMENT_ICONS, ANNOUNCEMENT_ROLES } from "../../config";
import { createAnnouncement } from "../../services/api/core";
import AnnouncementIcon from "./AnnouncementIcon";

const ROLE_LABELS = {
  crew: "Crew",
  manager: "Manager",
  admin: "Admin",
  executive: "Executive",
};

const EMPTY_FORM = {
  title: "",
  body: "",
  expires: "",
  icon: "rs",
  showTo: [...ANNOUNCEMENT_ROLES],
};

/**
 * Executive form to create an AppAnnouncement.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {Function} props.onClose Close the modal.
 * @returns {JSX.Element} Create-announcement modal.
 */
const CreateAnnouncementModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Update one form field.
   *
   * @param {string} field Field name.
   * @param {string|boolean|string[]} value Next value.
   * @returns {void}
   */
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  /**
   * Toggle a role in ``showTo``.
   *
   * @param {string} role Role key.
   * @returns {void}
   */
  const toggleRole = (role) => {
    setForm((current) => {
      const selected = current.showTo.includes(role)
        ? current.showTo.filter((item) => item !== role)
        : [...current.showTo, role];
      return { ...current, showTo: selected };
    });
  };

  /**
   * Reset the form and close the modal.
   *
   * @returns {void}
   */
  const handleClose = () => {
    setForm(EMPTY_FORM);
    setError("");
    onClose();
  };

  /**
   * Create the announcement and close on success.
   *
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError("Title and body are required.");
      return;
    }
    if (!form.showTo.length) {
      setError("Select at least one role.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await createAnnouncement({
        title: form.title.trim(),
        body: form.body.trim(),
        expires: form.expires || null,
        icon: form.icon,
        show_to: form.showTo,
      });
      handleClose();
    } catch (submitError) {
      setError(
        submitError.response?.data?.message || "Unable to create announcement."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={handleClose} maxWidth={520}>
      <div className="p-6">
        <h2 className="pr-8 text-xl font-bold text-gray-900">
          Create Announcement
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Sent once to users whose accounts are active currently. Plain text and basic HTML is
          supported:{" "}
          <code className="text-xs">&lt;b&gt;</code>,{" "}
          <code className="text-xs">&lt;i&gt;</code>,{" "}
          <code className="text-xs">&lt;u&gt;</code>, lists, and{" "}
          <code className="text-xs">&lt;a href=&quot;https://...&quot;&gt;</code>.
        </p>

        <fieldset className="mt-6">
          <legend className="text-sm font-semibold text-gray-700">Icon</legend>
          <div className="mt-2 flex flex-row gap-2">
            {ANNOUNCEMENT_ICONS.map((iconKey) => {
              const selected = form.icon === iconKey;
              return (
                <button
                  key={iconKey}
                  type="button"
                  onClick={() => updateField("icon", iconKey)}
                  className={`flex h-12 w-12 items-center justify-center rounded-lg transition ${
                    selected
                      ? "bg-gray-200 ring-1 ring-gray-300"
                      : "bg-white hover:bg-gray-50"
                  }`}
                  aria-pressed={selected}
                  aria-label={`${iconKey} icon`}
                >
                  {iconKey === "rs" ? (
                    <AnnouncementIcon icon="rs" />
                  ) : (
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <AnnouncementIcon icon={iconKey} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="mt-6 block text-sm font-semibold text-gray-700">
          Title
          <input
            className="mt-2 block min-h-[42px] w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
            type="text"
            value={form.title}
            onChange={(event) => updateField("title", event.target.value)}
            maxLength={255}
          />
        </label>

        <label className="mt-4 block text-sm font-semibold text-gray-700">
          Body
          <textarea
            className="mt-2 block min-h-[120px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
            value={form.body}
            onChange={(event) => updateField("body", event.target.value)}
          />
        </label>

        {form.title || form.body ? (
          <div className="mt-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Preview
            </p>
            <MessageModal
              isOpen
              inline
              html
              title={form.title}
              message={form.body}
              icon={form.icon}
              onClose={() => {}}
            />
          </div>
        ) : null}

        <label className="mt-4 block text-sm font-semibold text-gray-700">
          Expires (optional)
          <input
            className="mt-2 block min-h-[42px] w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
            type="datetime-local"
            value={form.expires}
            onChange={(event) => updateField("expires", event.target.value)}
          />
        </label>

        <fieldset className="mt-4">
          <legend className="text-sm font-semibold text-gray-700">Show to</legend>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {ANNOUNCEMENT_ROLES.map((role) => (
              <label
                key={role}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={form.showTo.includes(role)}
                  onChange={() => toggleRole(role)}
                />
                {ROLE_LABELS[role] || role}
              </label>
            ))}
          </div>
        </fieldset>

        {error ? (
          <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="cancel" text="Cancel" onClick={handleClose} />
          <Button
            variant={isSubmitting ? "disabled" : "primary"}
            text={isSubmitting ? "Creating..." : "Create"}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CreateAnnouncementModal;
